import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import classes from './Income.module.css';
import noStudioImage from "../../public/noImage.webp"
import { useGetUserIncome } from '../../hooks/finance/useGetUserIncome';
import { formatBerlinDate, formatBerlinDateTimeRange } from '../../util/bookingDateTime';
import { buildCoarseLocationText } from '../../util/locationPrivacy';

const formatDateTimeRange = (start, end) => {
  const dateTime = formatBerlinDateTimeRange(start, end, {
    dateOptions: {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    },
  });

  return dateTime ? `${dateTime.date}, ${dateTime.time}` : '';
};

const formatPrice = (price) => {
  return price.toFixed(2).replace('.', ',') + ' Euro';
};

const getMonthYear = (dateString) => {
  const month = formatBerlinDate(dateString, { month: 'long' });
  const year = formatBerlinDate(dateString, { year: 'numeric' });
  return `${month} ${year}`;
};

const groupByMonth = (bookings) => {
  const grouped = new Map();

  bookings.forEach((booking) => {
    const monthYear = getMonthYear(booking.startDateTime);
    if (!grouped.has(monthYear)) {
      grouped.set(monthYear, []);
    }
    grouped.get(monthYear).push(booking);
  });

  return grouped;
};

const IncomeCard = ({ booking, onBookingDetails, onReceipt }) => {
  const showReceiptButton =
    booking.paymentStatus === 'PAID' ||
    booking.paymentStatus === 'AUTHORIZED' ||
    booking.status === 'COMPLETED' ||
    booking.status === 'CONFIRMED';

  return (
    <div className={classes.transactionCard}>
      <div className={classes.transactionHeader}>
        <div className={classes.studioImageContainer}>
          <img
            src={booking?.Studio?.processedStudioImage || booking?.Studio?.image || noStudioImage}
            alt={booking?.Studio?.StudioName || 'Studio'}
            className={classes.studioImage}
          />
        </div>
        <div className={classes.transactionInfo}>
          <div className={classes.studioName}>
            {booking.Studio?.StudioName || 'Studio'}
          </div>
          <div className={classes.studioLocation}>
            {buildCoarseLocationText(booking.Studio)}
          </div>
        </div>
      </div>

      <div className={classes.transactionDivider} />

      <div className={classes.transactionDetails}>
        <div className={classes.transactionDate}>
          {formatDateTimeRange(booking.startDateTime, booking.endDateTime)}
        </div>
        <div className={classes.transactionAmount}>
          {formatPrice(booking.totalPrice)}
        </div>
      </div>

      <div className={classes.buttonContainer}>
        <button
          type="button"
          className={`${classes.actionButton} mediumLarge`}
          onClick={onBookingDetails}
        >
          Buchungsdetails
        </button>
        {showReceiptButton && (
          <button
            type="button"
            className={`${classes.actionButton} mediumLarge`}
            onClick={onReceipt}
          >
            Quittung
          </button>
        )}
      </div>
    </div>
  );
};

const Income = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state?.auth || {});
  const userId = user?.id;

  const [refreshing, setRefreshing] = useState(false);

  const {
    income,
    loading,
    error,
    refresh,
  } = useGetUserIncome(userId, { limit: 50 });

  const groupedData = useMemo(() => groupByMonth(income), [income]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }, [refresh]);

  const handleBookingDetails = (booking) => {
    navigate(`/creator/details/${booking.id}`);
  };

  const handleReceipt = (booking) => {
    navigate(`/receipt/${booking.id}`, { state: { type: 'income' } });
  };

  if (error && !loading && income.length === 0) {
    return (
      <div className={classes.container}>
        <div className={classes.header}>
          <div style={{cursor: 'pointer'}} onClick={() => navigate(-1)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="22" viewBox="0 0 13 22" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M12.4609 20.0297L10.9309 21.457L0.960938 10.5378L10.9309 0.457031L12.4017 1.9442L3.84334 10.5977L12.4609 20.0297Z" fill="#101010"/>
              </svg>
          </div>
        </div>
        <div className={classes.errorContainer}>
          <div className={classes.errorTitle}>Fehler beim Laden</div>
          <div className={classes.errorMessage}>
            {error.message || 'Daten konnten nicht geladen werden'}
          </div>
          <button
            type="button"
            className={classes.retryButton}
            onClick={onRefresh}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div style={{cursor: 'pointer'}} onClick={() => navigate(-1)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="22" viewBox="0 0 13 22" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M12.4609 20.0297L10.9309 21.457L0.960938 10.5378L10.9309 0.457031L12.4017 1.9442L3.84334 10.5977L12.4609 20.0297Z" fill="#101010"/>
              </svg>
        </div>
        <div className={classes.headerTitle} />
      </div>

      <div className={classes.scrollContainer}>
        <h1 className={classes.screenTitle}>Einkünfte</h1>

        {loading && income.length === 0 ? (
          <div className={classes.skeletonContainer}>
            <div className={classes.skeletonCard} />
            <div className={classes.skeletonCard} />
          </div>
        ) : income.length === 0 ? (
          <div className={classes.emptyContainer}>
            <div className={classes.emptyText}>Keine Einkünfte vorhanden</div>
            <div className={classes.emptySubText}>
              Hier erscheinen deine Einkünfte aus Studiobuchungen, sobald Gäste
              bei dir buchen.
            </div>
          </div>
        ) : (
          Array.from(groupedData.entries()).map(([monthYear, bookings]) => (
            <div key={monthYear} className={classes.monthGroup}>
              <div className={classes.monthTitle}>{monthYear}</div>
              {bookings.map((booking) => (
                <IncomeCard
                  key={booking.id}
                  booking={booking}
                  onBookingDetails={() => handleBookingDetails(booking)}
                  onReceipt={() => handleReceipt(booking)}
                />
              ))}
            </div>
          ))
        )}

        <div className={classes.bottomSpacer} />
      </div>
    </div>
  );
};

export default Income;
